import Component from '@glimmer/component';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { on } from '@ember/modifier';
import { get } from '@ember/helper';
import { macroCondition, isDevelopingApp } from '@embroider/macros';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import { eq } from 'ember-truth-helpers';
import { FileState } from 'ember-file-upload';
import fileQueue from 'ember-file-upload/helpers/file-queue';
import FileDropzone from 'ember-file-upload/components/file-dropzone';
import {
  HdsPageHeader,
  HdsCardContainer,
  HdsTextDisplay,
  HdsTextBody,
  HdsIconTile,
  HdsLinkStandalone,
  HdsReveal,
  HdsSeparator,
  HdsButton,
  HdsTable,
  HdsIcon,
  HdsBadge,
  HdsTooltipButton,
} from '@hashicorp/design-system-components/components';
import {
  CoreLayoutMain,
  BreadcrumbsContainer,
  Flex,
  CoreProgressBar,
  Time,
} from 'core/components';
import { optionForSourceState, formatFileSize } from 'core/helpers';
import { boxMargin, boxPadding } from 'core/modifiers';
import config from 'hcp/config/environment';

const allowedTypes = ['application/json'];
const UPLOAD_PROGRESS_COLOR_MAPPING = {
  [FileState.Aborted]: 'warning',
  [FileState.Failed]: 'critical',
  [FileState.Queued]: null, // No loading bar progress yet.
  [FileState.TimedOut]: 'warning',
  [FileState.Uploaded]: 'success',
  [FileState.Uploading]: 'action',
};
const CENSUS_API_ENDPOINTS = {
  [config.DEPLOY_TARGETS.DEV]:
    'https://staging.reporting.hashicorp.services/v2/',
  [config.DEPLOY_TARGETS.INT]:
    'https://staging.reporting.hashicorp.services/v2/',
  [config.DEPLOY_TARGETS.PROD]: 'https://reporting.hashicorp.services/v2/',
};

/**
 * POSTs the contents of a selected file upload to a specified URL. This is used
 * in conjunction and as a patch to use with ember-file-upload's multi-file
 * select addon.
 *
 * @async
 * @function postDirectlyToUrl
 * @param {string} url - The URL to which the file will be posted.
 * @param {Object} [options={ method: 'POST' }] - The options for the fetch
 *     request, defaults to POST method.
 * @returns {Promise<Object>} A promise that resolves to the object itself after
 *     updating its state based on the response.
 *
 * The function first converts the file to text and sets the object's state to
 * the value of FileState.Uploading.
 * It then performs a fetch request to the specified URL using the provided
 * options and the file text as the body.
 * If the response status is any 200 state then the state is set to
 * FileState.Uploaded, 'isUploadComplete' is set to true, and 'progress' is set
 * to 100 to mimic a 100% complete uplpoad.
 * If the response status is outside this range, the state is set to
 * FileState.Failed, 'isUploadComplete' is set to false, and 'progress' is set
 * to 5 to mimic a failed upload with a small amount of progress bar visible.
 * Regardless of the outcome, the response is stored in 'this.data'.
 *
 * @throws {Error} Throws an error if the fetch request fails.
 */
async function postDirectlyToUrl(url, options = { method: 'POST' }) {
  const body = await this.file.text();
  let response;

  this.state = FileState.Uploading;

  try {
    response = await fetch(url, {
      ...options,
      body,
    });

    if (response.status >= 200 && response.status <= 299) {
      this.state = FileState.Uploaded;
      this.isUploadComplete = true;
      this.progress = 100;
    } else {
      this.state = FileState.Failed;
      this.isUploadComplete = false;
      this.progress = 5;
    }
  } catch (e) {
    this.state = FileState.Failed;
    this.isUploadComplete = false;
    this.progress = 5;

    if (macroCondition(isDevelopingApp())) {
      console.error('Census report failed to upload:', e);
    }
  }

  this.data = response;

  return this;
}

const FILE_STATE_REJECTED = 'REJECTED';

/**
 *
 * `PageCensusReportsCreate` "uploads" documents for census processing.
 *
 *
 * ```
 * <Page::Census::Reports::Create />
 * ```
 *
 * @class PageCensusReportsCreate
 *
 */
export default class PageCensusReportsCreate extends Component {
  @service fileQueue;
  @service('config') appConfig;

  @tracked uploadedFiles = [];

  baseServiceHost =
    CENSUS_API_ENDPOINTS[config.environment] ??
    CENSUS_API_ENDPOINTS[config.DEPLOY_TARGETS.DEV];

  FileStateFailed = FileState.Failed;
  FileStateRejected = FILE_STATE_REJECTED;

  UPLOAD_PROGRESS_COLOR_MAPPING = UPLOAD_PROGRESS_COLOR_MAPPING;

  get queue() {
    return this.fileQueue.find('census-reports');
  }

  triggerFileInput(e) {
    e.preventDefault();

    const fileInput = document.getElementById('census-file-upload');

    if (fileInput) {
      fileInput.click();
    }
  }

  addToQueue = (file) => {
    // Validate that the file type is allowed.
    const isValid = this.validateFile(file);

    // Add the file to the queue and attach metadata.
    file.queue = this.queue;
    file.state = isValid ? FileState.Queued : FILE_STATE_REJECTED;
    file.timestamp = new Date();

    // Push the file to the top of the files that will display in the table.
    this.uploadedFiles = [file, ...this.uploadedFiles];

    // If it's valid, send it off.
    if (isValid) {
      return this.uploadReport.perform(file);
    }
  };

  uploadReport = task({ maxConcurrency: 3, enqueue: true }, async (file) => {
    // Patch the upload function with ours that simply issues a POST http call
    // to the census URL. The default `upload` method does a traditional file
    // upload.
    file.upload = postDirectlyToUrl;
    await file.upload(`${this.baseServiceHost}`);
  });

  validateFile(file) {
    return allowedTypes.includes(file.type);
  }
  <template>
    <CoreLayoutMain
      class="census-reports-create"
      data-test-census-reports-create-container
    >
      <:header>
        <div {{boxMargin "0 0 lg 0"}}>
          <HdsPageHeader as |PH|>
            <PH.Title data-test-census-reports-create-title>
              {{t
                "components.page.census.reports.create.license-utilization-reporting"
              }}
            </PH.Title>
            <PH.Breadcrumb>
              <BreadcrumbsContainer />
            </PH.Breadcrumb>
            <PH.IconTile @icon="file-text" />
          </HdsPageHeader>
        </div>
      </:header>
      <:body>
        <Flex @direction="column" @gap="xl">
          <div class="report-card">
            <HdsCardContainer
              @level="base"
              @hasBorder={{true}}
              class="report-card--container"
              {{boxPadding "lg"}}
              data-test-census-reports-create-vault-card
            >
              <div class="report-card__details">
                <div {{boxPadding "0 0 md 0"}}>
                  <HdsTextDisplay
                    @tag="h2"
                    @size="400"
                    @weight="bold"
                    data-test-census-reports-create-automated-reports-title
                  >
                    {{t
                      "components.page.census.reports.create.automate-reports"
                    }}
                  </HdsTextDisplay>
                </div>
                <HdsTextBody
                  @weight="medium"
                  data-test-census-reports-create-automated-reports-description
                >
                  {{t
                    "components.page.census.reports.create.automate-reports-description"
                  }}
                </HdsTextBody>
              </div>
              <div class="report-card__links--container">
                <div class="report-card__links">
                  <HdsIconTile @logo="vault" @size="small" />
                  <HdsLinkStandalone
                    @href="https://developer.hashicorp.com/vault/docs/license/utilization/auto-reporting"
                    @icon="external-link"
                    @iconPosition="trailing"
                    @isHrefExternal={{true}}
                    @text={{t
                      "components.page.census.reports.create.vault-instructions"
                    }}
                    data-test-census-reports-create-automated-vault-link
                  />
                </div>
                <div class="report-card__links">
                  <HdsIconTile @logo="terraform" @size="small" />
                  <HdsLinkStandalone
                    @href="https://developer.hashicorp.com/terraform/enterprise/deploy/replicated/administration/license/automated-license-utilization-reporting"
                    @icon="external-link"
                    @iconPosition="trailing"
                    @isHrefExternal={{true}}
                    @text={{t
                      "components.page.census.reports.create.terraform-instructions"
                    }}
                    data-test-census-reports-create-automated-terraform-link
                  />
                </div>
                <HdsReveal
                  @text={{t
                    "components.page.census.reports.create.more-products"
                  }}
                >
                  <div class="report-card__links">
                    <HdsIconTile @logo="boundary" @size="small" />
                    <HdsLinkStandalone
                      @href="https://developer.hashicorp.com/boundary/docs/enterprise/automated-license-reporting"
                      @icon="external-link"
                      @iconPosition="trailing"
                      @isHrefExternal={{true}}
                      @text={{t
                        "components.page.census.reports.create.boundary-instructions"
                      }}
                      data-test-census-reports-create-automated-boundary-link
                    />
                  </div>
                  <div class="report-card__links">
                    <HdsIconTile @logo="nomad" @size="small" />
                    <HdsLinkStandalone
                      @href="https://developer.hashicorp.com/nomad/docs/enterprise/license/utilization-reporting"
                      @icon="external-link"
                      @iconPosition="trailing"
                      @isHrefExternal={{true}}
                      @text={{t
                        "components.page.census.reports.create.nomad-instructions"
                      }}
                      data-test-census-reports-create-automated-nomad-link
                    />
                  </div>
                  <div class="report-card__links">
                    <HdsIconTile @logo="consul" @size="small" />
                    <HdsLinkStandalone
                      @href="https://developer.hashicorp.com/consul/docs/enterprise/license/reporting"
                      @icon="external-link"
                      @iconPosition="trailing"
                      @isHrefExternal={{true}}
                      @text={{t
                        "components.page.census.reports.create.consul-instructions"
                      }}
                      data-test-census-reports-create-automated-consul-link
                    />
                  </div>
                </HdsReveal>
              </div>
            </HdsCardContainer>
          </div>

          {{#let
            (fileQueue name="census-reports" onFileAdded=this.addToQueue)
            as |queue|
          }}
            <FileDropzone
              @queue={{queue}}
              data-test-census-reports-create-dropzone
              as |dropzone|
            >
              <HdsCardContainer
                @background={{if
                  dropzone.active
                  "neutral-secondary"
                  "neutral-primary"
                }}
                @hasBorder={{true}}
                class="report-card--container"
                {{boxPadding "xl"}}
                data-test-census-reports-create-upload-container
              >
                <div class="report-card__details">
                  <div {{boxPadding "0 0 md 0"}}>
                    <HdsTextDisplay
                      @tag="h2"
                      @size="400"
                      @weight="bold"
                      data-test-census-reports-create-manual-reports-title
                    >
                      {{t
                        "components.page.census.reports.create.manual-reports"
                      }}
                    </HdsTextDisplay>
                  </div>
                  <HdsTextBody
                    @weight="medium"
                    data-test-census-reports-create-manual-reports-description
                  >
                    {{t
                      "components.page.census.reports.create.manual-reports-description"
                    }}
                  </HdsTextBody>
                </div>
                <div class="report-card__links--container">
                  <div class="report-card__links">
                    <HdsIconTile @logo="vault" @size="small" />
                    <HdsLinkStandalone
                      @href="https://developer.hashicorp.com/vault/docs/v1.16.x/enterprise/license/manual-reporting"
                      @icon="external-link"
                      @iconPosition="trailing"
                      @isHrefExternal={{true}}
                      @text={{t
                        "components.page.census.reports.create.vault-instructions"
                      }}
                      data-test-census-reports-create-manual-vault-link
                    />
                  </div>
                  <div class="report-card__links">
                    <HdsIconTile @logo="terraform" @size="small" />
                    <HdsLinkStandalone
                      @href="https://developer.hashicorp.com/terraform/enterprise/deploy/manage/product-report#manually-reporting-product-usage-in-air-gapped-environments"
                      @icon="external-link"
                      @iconPosition="trailing"
                      @isHrefExternal={{true}}
                      @text={{t
                        "components.page.census.reports.create.terraform-instructions"
                      }}
                      data-test-census-reports-create-manual-terraform-link
                    />
                  </div>
                </div>
                <div class="report-card__upload">
                  <HdsSeparator />
                  {{#if dropzone.supported}}
                    <Flex
                      @alignItems="center"
                      @justifyContent="center"
                      @gap="sm"
                    >
                      <HdsTextDisplay
                        data-test-census-reports-create-upload-message
                      >
                        {{t
                          "components.page.census.reports.create.drop-files-here"
                        }}
                      </HdsTextDisplay>
                      <HdsButton
                        @color="primary"
                        @icon="upload"
                        @text={{t
                          "components.page.census.reports.create.choose-files"
                        }}
                        {{on "click" this.triggerFileInput}}
                        data-test-census-reports-create-upload-action
                      />
                      <input
                        class="hcp-visually-hidden"
                        id="census-file-upload"
                        type="file"
                        multiple
                        {{queue.selectFile}}
                        data-test-census-reports-create-upload-input
                      />
                    </Flex>
                  {{/if}}
                </div>
              </HdsCardContainer>
            </FileDropzone>
          {{/let}}

          {{#let this.uploadedFiles as |files|}}
            {{#if files}}
              <HdsTable
                @caption={{t "components.page.census.reports.create.reports"}}
                data-test-census-reports-create-upload-table
              >
                <:head as |H|>
                  <H.Tr>
                    <H.Th
                      data-test-census-reports-create-upload-table-file-header
                    >{{t "components.page.census.reports.create.file"}}</H.Th>
                    <H.Th
                      data-test-census-reports-create-upload-table-size-header
                    >{{t "components.page.census.reports.create.size"}}</H.Th>
                    <H.Th
                      data-test-census-reports-create-upload-table-status-header
                    >{{t "components.page.census.reports.create.status"}}</H.Th>
                    <H.Th
                      data-test-census-reports-create-upload-table-progress-header
                    >{{t
                        "components.page.census.reports.create.progress"
                      }}</H.Th>
                    <H.Th
                      data-test-census-reports-create-upload-table-upload-time-header
                    >{{t
                        "components.page.census.reports.create.upload-time"
                      }}</H.Th>
                  </H.Tr>
                </:head>
                <:body as |B|>
                  {{#each files as |file|}}
                    <B.Tr
                      data-test-census-reports-create-upload-table-row={{file.timestamp}}
                    >
                      <B.Td
                        data-test-census-reports-create-upload-table-cell
                        data-test-census-reports-create-upload-table-cell-file-name
                      >
                        <Flex @alignItems="center" @direction="row" @gap="xs">
                          <HdsIcon
                            data-test-census-reports-create-upload-file-icon
                            @name="file-text"
                            @isInline={{true}}
                          />
                          <span
                            data-test-census-reports-create-upload-file-name
                          >{{file.name}}</span>

                          {{#if file.modified}}
                            {{#let
                              (optionForSourceState
                                "census-reporting" "modified"
                              )
                              as |option|
                            }}
                              <HdsBadge
                                @text={{t option.text}}
                                @color={{option.color}}
                                @icon={{option.icon}}
                                @isIconOnly={{option.isIconOnly}}
                                @size={{option.size}}
                                @type={{option.type}}
                              />
                            {{/let}}
                          {{/if}}
                        </Flex>
                      </B.Td>
                      <B.Td
                        data-test-census-reports-create-upload-table-cell
                        data-test-census-reports-create-upload-table-cell-file-size
                      >{{formatFileSize file.size}}</B.Td>
                      <B.Td
                        data-test-census-reports-create-upload-table-cell
                        data-test-census-reports-create-upload-table-cell-file-status
                      >
                        <Flex @alignItems="center" @direction="row" @gap="2xs">
                          {{#let
                            (optionForSourceState "census-reporting" file.state)
                            as |option|
                          }}
                            <HdsBadge
                              @text={{t option.text}}
                              @color={{option.color}}
                              @icon={{option.icon}}
                              @isIconOnly={{option.isIconOnly}}
                              @size={{option.size}}
                              @type={{option.type}}
                            />
                          {{/let}}
                          {{#if (eq file.state this.FileStateFailed)}}
                            <HdsTooltipButton
                              @text={{t
                                "components.page.census.reports.create.contact-support"
                              }}
                            >
                              <HdsIcon
                                @name="alert-circle"
                                @color="var(--token-color-foreground-action)"
                                @isInline={{true}}
                              />
                            </HdsTooltipButton>
                          {{/if}}
                          {{#if (eq file.state this.FileStateRejected)}}
                            <HdsTooltipButton
                              @text={{t
                                "components.page.census.reports.create.file-type-not-supported"
                              }}
                            >
                              <HdsIcon
                                @name="alert-circle"
                                @color="var(--token-color-foreground-action)"
                                @isInline={{true}}
                              />
                            </HdsTooltipButton>
                          {{/if}}
                        </Flex>
                      </B.Td>
                      <B.Td
                        data-test-census-reports-create-upload-table-cell
                        data-test-census-reports-create-upload-table-cell-file-progress
                      >
                        <Flex
                          @alignItems="middle"
                          @basis="zero-percent"
                          @direction="column"
                          @grow={{1}}
                          @shrink={{1}}
                          @justifyContent="center"
                          {{boxMargin "xs 0 0 0"}}
                        >
                          <CoreProgressBar
                            @color={{get
                              this.UPLOAD_PROGRESS_COLOR_MAPPING
                              file.state
                            }}
                            @max="100"
                            @value={{file.progress}}
                          />
                        </Flex>
                      </B.Td>
                      <B.Td
                        data-test-census-reports-create-upload-table-cell
                        data-test-census-reports-create-upload-table-cell-file-timestamp
                      >
                        <Time
                          @date={{file.timestamp}}
                          @display="friendly-local"
                        />
                      </B.Td>
                    </B.Tr>
                  {{/each}}
                </:body>
              </HdsTable>
            {{/if}}
          {{/let}}
        </Flex>
      </:body>
    </CoreLayoutMain>
  </template>
}
